import { Container, Graphics } from 'pixi.js';

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - Math.min(1, Math.max(0, t)), 3);
}

function lerpColor(a: number, b: number, t: number): number {
  const u = Math.min(1, Math.max(0, t));
  const ar = (a >> 16) & 255;
  const ag = (a >> 8) & 255;
  const ab = a & 255;
  const br = (b >> 16) & 255;
  const bg = (b >> 8) & 255;
  const bb = b & 255;
  const r = Math.round(ar + (br - ar) * u);
  const g = Math.round(ag + (bg - ag) * u);
  const bl = Math.round(ab + (bb - ab) * u);
  return (r << 16) | (g << 8) | bl;
}

/**
 * ALIGN → CONTACT — black square scales to cover the screen, flash-fades to tan, reveal contact.
 */
export class SquareScaleTransition {
  readonly root = new Container();
  private squareLayer = new Container();
  private gfx = new Graphics();
  private origin = { x: 0, y: 0 };
  private angle = 0;
  private t = 0;
  private expandDur = 0.58;
  private flashDur = 0.26;
  private blackColor = 0x121820;
  private tanColor = 0xf3ebe0;
  private sourceHalf = 36;
  private coverHalf = 1;

  constructor() {
    this.root.addChild(this.squareLayer);
    this.squareLayer.addChild(this.gfx);
  }

  begin(
    x: number,
    y: number,
    w: number,
    h: number,
    blackColor: number,
    tanColor: number,
    angle: number,
    sourceSize: number,
    reducedMotion: boolean,
  ): void {
    this.origin = { x, y };
    this.angle = angle;
    this.blackColor = blackColor;
    this.tanColor = tanColor;
    this.sourceHalf = sourceSize * 0.5;
    this.coverHalf = this.coverHalfExtent(x, y, angle, w, h);
    this.expandDur = reducedMotion ? 0.2 : 0.58;
    this.flashDur = reducedMotion ? 0.1 : 0.26;
    this.t = 0;
  }

  update(dt: number): boolean {
    this.t += dt;
    const total = this.expandDur + this.flashDur;

    this.gfx.clear();

    if (this.t < this.expandDur) {
      const e = easeOutCubic(this.t / this.expandDur);
      const half = this.sourceHalf + (this.coverHalf - this.sourceHalf) * e;
      this.drawSquare(half, this.blackColor, 1);
    } else {
      const flashT = Math.min(1, (this.t - this.expandDur) / this.flashDur);
      const e = easeOutCubic(flashT);
      const color = lerpColor(this.blackColor, this.tanColor, e);
      this.drawSquare(this.coverHalf * 1.02, color, 1);
    }

    return this.t >= total;
  }

  destroy(): void {
    this.gfx.clear();
    this.root.destroy({ children: true });
  }

  private drawSquare(half: number, color: number, alpha: number): void {
    this.squareLayer.position.set(this.origin.x, this.origin.y);
    this.squareLayer.rotation = this.angle;
    this.gfx.rect(-half, -half, half * 2, half * 2).fill({ color, alpha });
  }

  /** Half-extent for a rotated square to cover the viewport from origin. */
  private coverHalfExtent(ox: number, oy: number, angle: number, w: number, h: number): number {
    const corners: Array<[number, number]> = [
      [0, 0],
      [w, 0],
      [w, h],
      [0, h],
    ];
    const c = Math.cos(-angle);
    const s = Math.sin(-angle);
    let max = 0;
    for (const [cx, cy] of corners) {
      const dx = cx - ox;
      const dy = cy - oy;
      const lx = dx * c - dy * s;
      const ly = dx * s + dy * c;
      max = Math.max(max, Math.abs(lx), Math.abs(ly));
    }
    return max * 1.04;
  }
}
